import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
  View,
  Text,
  ScrollView,
  TextInput,
  Pressable,
  Platform,
  TouchableOpacity,
  useWindowDimensions,
  Alert,
  Modal,
  ActivityIndicator,
  StyleSheet,
  TouchableWithoutFeedback,
} from 'react-native';
import Svg, { Path, Line } from 'react-native-svg';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { RootState } from '../../store';
import { FontFamily } from '../../constants/fonts';
import { AdminColors } from '../Admin/constants/adminColors';
import { hasDeliveryRPMFullAccess } from './harborRoles';
import PTLanguageToggle from '../PerfectTruck/PTLanguageToggle';
import HarborDataTable from './HarborDataTable';
import HarborDropdown from './HarborDropdown';
import RoadblockRecordModal from './RoadblockRecordModal';
import { useToast } from '../../components/LhToastContainer';
import { styles as baseStyles, tbadge as baseTbadge } from './RouteRoadblocksScreen.styles';
import { formatDate as formatDateUtil, formatTimeAmPm } from '../PerfectTruck/perfectTruckUtils';
import {
  validateRoadblockSscc,
  fetchRoadblockCustomers,
  fetchRecentRoadblockSubmissions,
  fetchRouteRoadblocks,
  parseError,
  CustomerOption,
  RoadblockSubmissionsParams,
} from '../../api/deliveryRpmApi';
import { resolveReasonLabel } from './deliveryRpmReasonCodeUtils';
import { fetchRouteNumbersByLocation } from '../PerfectTruck/perfectTruckApi';
import { useDarkStyles, useDarkColor } from '../../theme/useDarkStyles';

// ─── Types ────────────────────────────────────────────────────────────────────

type RoadblockType = 'Stop Issue' | 'Route Issue';

export interface RoadblockRecord {
  id: string;
  type: RoadblockType;
  route: string;
  sscc: string;
  date: string; // ISO YYYY-MM-DD
  reason: string;
  details: string;
  driver: string;
  customer?: string;
  submissionTimeStamp?: string; // ISO datetime
}

type SortDirection = 'asc' | 'desc';
type QuickFilter = 'all' | 'today' | 'last7' | 'stop' | 'route';

// Maps UI column keys to the backend sortBy field names
const SORT_KEY_MAP: Record<string, string> = {
  type: 'type',
  route: 'route',
  sscc: 'SSCC',
  date: 'deliveryDate',
  reason: 'reason',
  details: 'details',
  driver: 'driver',
  submissionTimeStamp: 'createdDate',
};

// type RouteTimeframe = 'present' | 'historical';

type StopIssueCustomer = {
  cof: string;
  name: string;
  sscc?: string;
  ssccList?: string[];
};

interface RouteRoadblocksScreenProps {
  onNavigate?: (screen: string, params?: Record<string, any>) => void;
  roleName?: string | null;
  /** Incremented by DeliveryRPMScreen each time we navigate back here, forcing a table refresh. */
  refreshKey?: number;
  /** Success message from a Route/Stop Issue submission, shown as a toast once and then cleared. */
  toastMessage?: string | null;
  onToastShown?: () => void;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const todayIso = () => new Date().toISOString().slice(0, 10);
const TODAY = todayIso();

// ─── Helpers ──────────────────────────────────────────────────────────────────

const formatDate = (iso: string) => {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return `${m}/${d}/${y}`;
};

const formatDateShort = (iso: string) => {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return `${m}/${d}/${y.slice(2)}`;
};

/** Format ISO datetime (UTC) to device local timezone — e.g. "05/18/2026 06:33 AM PDT" */
const formatDateLocal = (iso: string): string => {
  if (!iso) return '—';
  // Extract date portion (MM/DD/YYYY) using the utility
  const dateStr = formatDateUtil(iso);
  if (dateStr === '--') return '—';
  // Get time with timezone using the utility (returns "HH:MM AM/PM TZ")
  const timeStr = formatTimeAmPm(iso);
  if (timeStr === '--') return '—';
  return `${dateStr} ${timeStr}`;
};

const formatCustomerDisplay = (cof?: string, name?: string, fallback?: string) => {
  const cofText = (cof ?? '').trim();
  const nameText = (name ?? '').trim();
  if (cofText && nameText) return `${cofText} - ${nameText}`;
  if (cofText) return cofText;
  if (nameText) return nameText;
  return fallback ?? '';
};

const PAGE_SIZE = 100;

// Returns page indices (0-based) to display, inserting '...' for gaps
const getPageNumbers = (current: number, total: number): (number | '...')[] => {
  if (total <= 5) return Array.from({ length: total }, (_, i) => i);
  const pages: (number | '...')[] = [0];
  if (current > 2) pages.push('...');
  for (let i = Math.max(1, current - 1); i <= Math.min(total - 2, current + 1); i++) pages.push(i);
  if (current < total - 3) pages.push('...');
  pages.push(total - 1);
  return pages;
};

const getDetailsTooltipText = (value?: string) => (value ?? '').replace(/\s+/g, ' ').trim();

const DetailsTooltip: React.FC<{
  visible: boolean;
  position: { x: number; y: number } | null;
  text: string;
}> = ({ visible, position, text }) => {
  const dc = useDarkColor();
  if (Platform.OS !== 'web' || !visible || !position || !text) return null;
  const win: any = typeof globalThis !== 'undefined' ? (globalThis as any) : {};
  if (!win.document) return null;

  const overlay = (
    <View
      style={{
        position: 'fixed' as any,
        left: position.x + 14,
        top: position.y - 10,
        maxWidth: 360,
        zIndex: 2147483647,
        pointerEvents: 'none' as any,
      }}
    >
      <View
        style={{
          paddingHorizontal: 12,
          paddingVertical: 8,
          borderRadius: 6,
          backgroundColor: dc('#1C1C1C', 'bg'),
        }}
      >
        <Text style={{ color: '#FFFFFF', fontSize: 12, fontWeight: '400', lineHeight: 18 }}>
          {text}
        </Text>
      </View>
    </View>
  );

  return createPortal(overlay, win.document.body);
};

// ─── Icons ────────────────────────────────────────────────────────────────────

const GoArrowIcon = ({ ready }: { ready: boolean }) => (
  <Svg width={16} height={16} viewBox="0 0 24 24" fill="none">
    <Path d="M8 5l8 7-8 7" stroke={ready ? '#fff' : '#BDBDBD'} strokeWidth={2.6} strokeLinecap="round" strokeLinejoin="round" />
  </Svg>
);

const EyeIcon = () => {
  const dc = useDarkColor();
  return (
    <Svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke={dc("#005CBC")} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <Path d="M2 12s3.5-6.5 10-6.5S22 12 22 12s-3.5 6.5-10 6.5S2 12 2 12Z" />
      <Path d="M12 9.4a2.6 2.6 0 1 0 0 5.2 2.6 2.6 0 0 0 0-5.2Z" />
    </Svg>
  );
};

// ─── Type badge ───────────────────────────────────────────────────────────────

const TypeBadge = ({ type, t }: { type: RoadblockType; t: (key: string) => string }) => {
  const tbadge = useDarkStyles(baseTbadge);
  const badgeLabel = type === 'Stop Issue' ? t('route_roadblocks.stop_issue') : t('route_roadblocks.route_issue');
  return (
    <View style={[tbadge.wrap, type === 'Stop Issue' ? tbadge.blue : tbadge.orange]}>
      <Text style={[tbadge.text, type === 'Stop Issue' ? tbadge.blueText : tbadge.orangeText]}>
        {badgeLabel}
      </Text>
    </View>
  );
};

// ─── Main screen ──────────────────────────────────────────────────────────────

const RouteRoadblocksScreen: React.FC<RouteRoadblocksScreenProps> = ({ onNavigate, roleName: roleProp, refreshKey = 0, toastMessage, onToastShown }) => {
  const dc = useDarkColor();
  const styles = useDarkStyles(baseStyles);
  const { t } = useTranslation('harbor');
  const { showToast } = useToast();
  const { width } = useWindowDimensions();
  const isMobile = width < 768;

  const storeRoleName = useSelector((state: RootState) => state.appData?.userInfo?.roleName);
  const selectedLocation = useSelector((state: RootState) => state.appData?.selectedLocation);
  const effectiveRole = roleProp ?? storeRoleName;
  const isFullAccess = hasDeliveryRPMFullAccess(effectiveRole);

  // ── Shared "not scheduled" modal state (used by both SSCC and Customer flows)
  const [ssccNotScheduledModal, setSsccNotScheduledModal] = useState(false);
  const [ssccNotScheduledMessage, setSsccNotScheduledMessage] = useState('');
  const [pendingCustomer, setPendingCustomer] = useState<StopIssueCustomer | null>(null);
  const [proceedValidating, setProceedValidating] = useState(false);

  // ── Customer state ────────────────────────────────────────────────────────
  const [customerText, setCustomerText] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState<StopIssueCustomer | null>(null);
  const [customerError, setCustomerError] = useState('');
  const [customerValidating, setCustomerValidating] = useState(false);
  const [customerDropOpen, setCustomerDropOpen] = useState(false);
  const [customerOptions, setCustomerOptions] = useState<CustomerOption[]>([]);
  const [customerEmptyMsg, setCustomerEmptyMsg] = useState<string | null>(null);
  const [customerLoading, setCustomerLoading] = useState(false);
  const customerDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const customerSelectingRef = useRef(false);
  const scrollRef = useRef<ScrollView>(null);
  const routeCardRef = useRef<View>(null);
  // Anchors the mobile Modal dropdown directly below the input (measured in window coords)
  const customerInputWrapRef = useRef<View>(null);
  const [customerDropPos, setCustomerDropPos] = useState({ top: 0, left: 0, width: 0 });
  const measureCustomerDropdown = () => {
    customerInputWrapRef.current?.measureInWindow((x: number, y: number, w: number, h: number) => {
      setCustomerDropPos({ top: y + h + 4, left: x, width: w });
    });
  };

  // ── Route section state ───────────────────────────────────────────────────
  const [selectedRoute, setSelectedRoute] = useState('');
  const [selectedRouteItem, setSelectedRouteItem] = useState<{ id: string; displayText: string } | null>(null);
  const [routeDropdownOpenTrigger, setRouteDropdownOpenTrigger] = useState(0);
  // const [routeTimeframe, setRouteTimeframe] = useState<RouteTimeframe>('present');
  const [availableRoutes, setAvailableRoutes] = useState<string[]>([]);
  const [isLoadingRoutes, setIsLoadingRoutes] = useState(false);
  const [beginLoading, setBeginLoading] = useState(false);
  const [beginError, setBeginError] = useState('');

  // ── Load routes from API ──────────────────────────────────────────────────
  const loadRoutes = useCallback(async () => {
    setIsLoadingRoutes(true);
    setAvailableRoutes([]);
    try {
      if (!selectedLocation?.name) {
        setAvailableRoutes([]);
        return;
      }
      const routes = await fetchRouteNumbersByLocation(selectedLocation.name);
      setAvailableRoutes(routes || []);
    } catch {
      setAvailableRoutes([]);
    } finally {
      setIsLoadingRoutes(false);
    }
  }, [selectedLocation?.name]);

  useEffect(() => {
    loadRoutes();
  }, [loadRoutes]);

  const routeOptions = useMemo(() => {
    return availableRoutes.map(routeName => ({ label: routeName, value: routeName }));
  }, [availableRoutes]);

  // ── Table state ───────────────────────────────────────────────────────────
  const [quickFilter, setQuickFilter] = useState<QuickFilter>('all');
  const [globalSearch, setGlobalSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [columnFilters, setColumnFilters] = useState<Record<string, string[]>>({});
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [allData, setAllData] = useState<RoadblockRecord[]>([]);
  const [totalElements, setTotalElements] = useState(0);
  const [tableLoading, setTableLoading] = useState(true);
  const [tableError, setTableError] = useState('');
  const [hoveredDetails, setHoveredDetails] = useState<{ text: string; x: number; y: number } | null>(null);

  // ── Server-side sort state ────────────────────────────────────────────────
  const [sortBy, setSortBy] = useState<string | undefined>(undefined);
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  // ── Date range filter state ───────────────────────────────────────────────
  const [dateFrom, setDateFrom] = useState<Date | null>(null);
  const [dateTo, setDateTo] = useState<Date | null>(null);

  // ── Record detail modal state ─────────────────────────────────────────────
  const [selectedRecordId, setSelectedRecordId] = useState<string | null>(null);
  const [reasonLabelsByType, setReasonLabelsByType] = useState<Record<RoadblockType, string[]>>({
    'Stop Issue': [],
    'Route Issue': [],
  });

  const roadblockRcMap = useSelector((state: RootState) => state.deliveryRpm.roadblockRcMap);
  // id → translated roadblockType ('Stop Issue' | 'Route Issue'), sourced from /config/route-roadblocks
  // for the currently selected app language. Drives the "Reason" column below.
  const roadblockTypeMap = useSelector((state: RootState) => state.deliveryRpm.roadblockTypeMap);

  // ── Load recent submissions ───────────────────────────────────────────────
  const loadRecentSubmissions = useCallback(async (overridePage?: number) => {
    setTableLoading(true);
    setTableError('');
    try {
      const toLocalDateStr = (d: Date) => {
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${y}-${m}-${day}`;
      };
      const fromIso = dateFrom ? toLocalDateStr(dateFrom) : undefined;
      const toIso = dateTo ? toLocalDateStr(dateTo) : undefined;

      const params: RoadblockSubmissionsParams = {
        page: overridePage ?? currentPage,
        pageSize,
        sortBy,
        sortDirection,
        search: debouncedSearch.trim() || undefined,
        fromDate: fromIso,
        toDate: toIso,
      };

      const colKeyToApiParam: Record<string, keyof RoadblockSubmissionsParams> = {
        type: 'type',
        route: 'route',
        sscc: 'SSCC',
        date: 'deliveryDate',
        // Note: reason UI shows translated labels, but backend filters by reasonCodeId.
        // We'll handle `reason` specially below and send `reasonCodeId` values.
        reason: 'reason',
        details: 'details',
        driver: 'driver',
      };
      for (const [col, apiKey] of Object.entries(colKeyToApiParam)) {
        const selected = columnFilters[col];
        if (selected && selected.length > 0) {
          if (col === 'reason') {
            // Map translated reason labels back to their numeric ids using roadblockRcMap
            const ids: number[] = [];
            const entries = Object.entries(roadblockRcMap);
            for (const label of selected) {
              const found = entries.find(([, v]) => v === label);
              if (found) ids.push(Number(found[0]));
            }
            if (ids.length > 0) {
              (params as any).reasonCodeId = ids;
            }
          } else {
            (params as any)[apiKey] = selected;
          }
        }
      }

      const res = await fetchRecentRoadblockSubmissions(params);
      const mapped: RoadblockRecord[] = res.data.map(s => ({
        id: String(s.id),
        type: (s.recordType ?? 'Stop Issue') as RoadblockType,
        route: s.localRoute ?? s.routeId ?? '—',
        sscc: s.sscc ?? '',
        date: s.deliveryDate ?? '',
        // Match submission.reasonCodeId → config/route-roadblocks.id → display translated `routeRoadblock`
        // (product wants the `routeRoadblock` label such as "Locked Gate", not the roadblockType)
        reason: roadblockRcMap[s.reasonCodeId] ?? resolveReasonLabel(s.reasonCodeId, roadblockTypeMap) ?? String(s.reasonCodeId),
        details: s.additionalDetails ?? '',
        driver: s.driverName ?? s.driverGpid ?? '',
        customer: s.customerName ?? '',
        submissionTimeStamp: s.createdDate ?? '',
      }));
      setAllData(mapped);
      setTotalElements(res.pagination?.totalElements ?? mapped.length);
    } catch (err) {
      setTableError(parseError(err));
    } finally {
      setTableLoading(false);
    }
  }, [roadblockRcMap, roadblockTypeMap, currentPage, pageSize, sortBy, sortDirection, debouncedSearch, columnFilters, dateFrom, dateTo]);

  useEffect(() => {
    // Reload when refreshKey or selected location changes so we show a
    // loading skeleton instead of stale data while the API call runs.
    loadRecentSubmissions();
  }, [loadRecentSubmissions, refreshKey, selectedLocation?.name]);

  // ── Show success toast for Route/Stop Issue submissions after navigating back ────
  useEffect(() => {
    if (!toastMessage) return;
    showToast({
      header: '',
      subtext: toastMessage,
      variant: 'success',
      position: 'top',
      duration: 4000,
    });
    onToastShown?.();
  }, [toastMessage, onToastShown, showToast]);

  useEffect(() => {
    let mounted = true;

    const loadReasonCatalog = async () => {
      try {
        const res = await fetchRouteRoadblocks({ limit: '500' });
        const stopReasons = new Set<string>();
        const routeReasons = new Set<string>();

        res.items.forEach(item => {
          const label = (item.routeRoadblock ?? '').trim();
          if (!label) return;
          if (item.roadblockType === 'Stop Issue') {
            stopReasons.add(label);
          } else {
            routeReasons.add(label);
          }
        });

        if (!mounted) return;
        setReasonLabelsByType({
          'Stop Issue': Array.from(stopReasons),
          'Route Issue': Array.from(routeReasons),
        });
      } catch {
        // Keep fallback behavior based on roadblockRcMap values.
      }
    };

    loadReasonCatalog();

    return () => {
      mounted = false;
    };
  }, []);

  // ── Customer handlers ─────────────────────────────────────────────────────
  const handleCustomerTextChange = (val: string) => {
    setCustomerText(val);
    setSelectedCustomer(null);
    setCustomerError('');

    if (!val.trim()) {
      setCustomerDropOpen(false);
      setCustomerOptions([]);
      setCustomerEmptyMsg(null);
      if (customerDebounceRef.current) clearTimeout(customerDebounceRef.current);
      return;
    }

    // Measure only on the closed→open transition; re-measuring every keystroke caused a mobile crash
    if (!customerDropOpen && Platform.OS !== 'web') measureCustomerDropdown();
    setCustomerDropOpen(true);
    if (customerDebounceRef.current) clearTimeout(customerDebounceRef.current);
    customerDebounceRef.current = setTimeout(async () => {
      setCustomerLoading(true);
      try {
        const res = await fetchRoadblockCustomers(val.trim(), selectedLocation?.name, 25);
        setCustomerOptions(res.customers);
        setCustomerEmptyMsg(res.emptyMessage);
      } catch {
        setCustomerOptions([]);
        setCustomerEmptyMsg(t('route_roadblocks.error_load_customers'));
      } finally {
        setCustomerLoading(false);
      }
    }, 3000);
  };

  const handleSelectCustomer = (opt: CustomerOption) => {
    const cof = opt.customerCofId ?? '';
    const name = opt.customerName ?? opt.displayLabel;
    const sscc = opt.sscc ?? opt.ssccList?.[0];
    setSelectedCustomer({
      cof,
      name,
      ...(sscc ? { sscc } : {}),
      ...(opt.ssccList?.length ? { ssccList: opt.ssccList } : {}),
    });
    setCustomerText(formatCustomerDisplay(cof, opt.customerName, opt.displayLabel));
    setCustomerDropOpen(false);
    setCustomerOptions([]);
    setCustomerError('');
  };

  const handleCustomerSubmit = async () => {
    if (!selectedCustomer) return;
    const customerSscc = selectedCustomer.sscc;
    if (!customerSscc) {
      onNavigate?.('StopIssueSubmit', { customer: selectedCustomer });
      return;
    }
    setCustomerValidating(true);
    setCustomerError('');
    try {
      const result = await validateRoadblockSscc({ sscc: customerSscc, proceedWhenNotScheduled: false });
      if (result.valid) {
        onNavigate?.('StopIssueSubmit', { customer: selectedCustomer });
        return;
      }
      if (result.requiresProceedConfirmation && result.canProceedWithOverride) {
        setPendingCustomer(selectedCustomer);
        setSsccNotScheduledMessage(t('route_roadblocks.error_sscc_not_scheduled_fallback'));
        setSsccNotScheduledModal(true);
        return;
      }
      setCustomerError(result.inlineError || result.bannerError || result.modalMessage || t('route_roadblocks.error_validate_sscc'));
    } catch (err) {
      setCustomerError(parseError(err));
    } finally {
      setCustomerValidating(false);
    }
  };

  // ── Proceed after "not scheduled" warning ─────────────────────────────────
  const handleProceed = async () => {
    if (!pendingCustomer?.sscc) {
      setSsccNotScheduledModal(false);
      setPendingCustomer(null);
      return;
    }
    setProceedValidating(true);
    try {
      const result = await validateRoadblockSscc({ sscc: pendingCustomer.sscc, proceedWhenNotScheduled: true });
      if (!result.valid) {
        const msg = result.inlineError || result.bannerError || result.modalMessage || t('route_roadblocks.error_validate_sscc');
        setSsccNotScheduledModal(false);
        setPendingCustomer(null);
        setCustomerError(msg);
        return;
      }
      setSsccNotScheduledModal(false);
      onNavigate?.('StopIssueSubmit', { customer: pendingCustomer, proceedWhenNotScheduled: true });
      setPendingCustomer(null);
    } catch (err) {
      const msg = parseError(err);
      setSsccNotScheduledModal(false);
      setPendingCustomer(null);
      setCustomerError(msg);
    } finally {
      setProceedValidating(false);
    }
  };

  // ── Route Issue Begin ─────────────────────────────────────────────────────
  const handleBegin = () => {
    if (!selectedRoute) return;
    onNavigate?.('RouteIssueSubmit', {
      route: selectedRoute,
      date: TODAY,
      location: selectedLocation?.name,
    });
  };

  // ── Clear all filters ─────────────────────────────────────────────────────
  const clearAllFilters = () => {
    setColumnFilters({});
    setGlobalSearch('');
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    setDebouncedSearch('');
    setQuickFilter('all');
    setDateFrom(null);
    setDateTo(null);
    setSortBy(undefined);
    setSortDirection('asc');
    setCurrentPage(0);
  };

  // ── Quick-filter pill handler ─────────────────────────────────────────────
  const handlePillPress = useCallback((key: QuickFilter) => {
    const next = quickFilter === key && key !== 'all' ? 'all' : key;
    setQuickFilter(next);
    setCurrentPage(0);

    if (next === 'all') {
      setDateFrom(null);
      setDateTo(null);
      setColumnFilters(prev => {
        const updated = { ...prev };
        delete updated.type;
        return updated;
      });
      return;
    }

    if (next === 'today') {
      const today = new Date();
      setDateFrom(today);
      setDateTo(today);
      setColumnFilters(prev => { const u = { ...prev }; delete u.type; return u; });
    } else if (next === 'last7') {
      const today = new Date();
      const sevenDaysAgo = new Date(Date.now() - 6 * 86400000);
      setDateFrom(sevenDaysAgo);
      setDateTo(today);
      setColumnFilters(prev => { const u = { ...prev }; delete u.type; return u; });
    } else if (next === 'stop') {
      setDateFrom(null);
      setDateTo(null);
      setColumnFilters(prev => ({ ...prev, type: ['Stop Issue'] }));
    } else if (next === 'route') {
      setDateFrom(null);
      setDateTo(null);
      setColumnFilters(prev => ({ ...prev, type: ['Route Issue'] }));
    }
  }, [quickFilter]);

  const filteredData = allData;
  const totalPages = Math.max(1, Math.ceil(totalElements / pageSize));

  const hasActiveFilters = Object.values(columnFilters).some(v => v.length > 0) || !!globalSearch.trim() || quickFilter !== 'all' || !!dateFrom || !!dateTo;

  const reasonFilterOptions = useMemo(() => {
    const selectedTypes = columnFilters.type ?? [];

    const toOptions = (labels: string[]) =>
      labels
        .filter(Boolean)
        .sort((a, b) => a.localeCompare(b))
        .map(label => ({ label, value: label }));

    if (selectedTypes.length === 1 && (selectedTypes[0] === 'Stop Issue' || selectedTypes[0] === 'Route Issue')) {
      const type = selectedTypes[0] as RoadblockType;
      const typed = reasonLabelsByType[type];
      if (typed.length > 0) {
        return toOptions([...typed]);
      }
    }

    return toOptions(Object.values(roadblockRcMap));
  }, [roadblockRcMap, columnFilters.type, reasonLabelsByType]);

  const columns = useMemo(() => [
    {
      columnKey: 'type',
      header: t('route_roadblocks.table_headers.type'),
      flexGrow: 1.5,
      sortable: true,
      filterOptions: [
        { label: t('route_roadblocks.stop_issue'), value: 'Stop Issue' },
        { label: t('route_roadblocks.route_issue'), value: 'Route Issue' },
      ],
      renderCell: (item: RoadblockRecord) => <TypeBadge type={item.type} t={t} />,
    },
    {
      columnKey: 'route',
      header: t('route_roadblocks.table_headers.route'),
      flexGrow: 1,
      sortable: true,
    },
    {
      columnKey: 'sscc',
      header: t('route_roadblocks.table_headers.sscc'),
      flexGrow: 2,
      sortable: true,
      formatter: (val: string) => val || '—',
    },
    {
      columnKey: 'date',
      header: t('route_roadblocks.table_headers.date'),
      flexGrow: 1,
      sortable: true,
      formatter: (val: string) => formatDate(val),
      dateRangeFilter: {
        fromDate: dateFrom,
        toDate: dateTo,
        onFromChange: (d: Date | null) => { setDateFrom(d); setCurrentPage(0); },
        onToChange: (d: Date | null) => { setDateTo(d); setCurrentPage(0); },
        onClear: () => { setDateFrom(null); setDateTo(null); setCurrentPage(0); },
      },
    },
    {
      columnKey: 'reason',
      header: t('route_roadblocks.table_headers.reason'),
      flexGrow: 2,
      sortable: true,
      filterOptions: reasonFilterOptions,
    },
    {
      columnKey: 'details',
      header: t('route_roadblocks.table_headers.details'),
      flexGrow: 3,
      sortable: true,
      formatter: (val: string) => getDetailsTooltipText(val).slice(0, 40),
      renderCell: (item: RoadblockRecord) => {
        const fullText = getDetailsTooltipText(item.details);
        const displayText = fullText.length > 100 ? `${fullText.slice(0, 100)}\u2026` : fullText;
        const webHandlers: any = Platform.OS === 'web'
          ? {
              onMouseEnter: (e: any) => {
                const x = e?.clientX ?? e?.nativeEvent?.clientX ?? 0;
                const y = e?.clientY ?? e?.nativeEvent?.clientY ?? 0;
                setHoveredDetails({ text: fullText, x, y });
              },
              onMouseMove: (e: any) => {
                const x = e?.clientX ?? e?.nativeEvent?.clientX ?? 0;
                const y = e?.clientY ?? e?.nativeEvent?.clientY ?? 0;
                setHoveredDetails((prev) => (prev ? { ...prev, x, y } : null));
              },
              onMouseLeave: () => setHoveredDetails(null),
            }
          : {};
        return (
          <View style={{ width: '100%' }} {...webHandlers}>
            <Text
              style={{
                fontSize: Platform.OS === 'web' ? 14 : 12,
                color: dc('#3A3A3A'),
                fontWeight: '400',
                fontFamily: FontFamily.regular,
              }}
            >
              {displayText || '—'}
            </Text>
          </View>
        );
      },
    },
    {
      columnKey: 'driver',
      header: t('route_roadblocks.table_headers.driver'),
      flexGrow: 1,
      sortable: true,
    },
    {
      columnKey: 'submissionTimeStamp',
      header: t('route_roadblocks.table_headers.submission_timestamp'),
      flexGrow: 1.5,
      sortable: true,
      formatter: formatDateLocal,
    },
  ], [dateFrom, dateTo, reasonFilterOptions, t]);

  const handleSort = useCallback((columnKey: string, direction: 'asc' | 'desc') => {
    const backendKey = SORT_KEY_MAP[columnKey];
    if (!backendKey) return;
    setSortBy(backendKey);
    setSortDirection(direction);
    setCurrentPage(0);
  }, []);

  const handleColumnFilter = useCallback((columnKey: string, values: string[]) => {
    setColumnFilters(prev => ({ ...prev, [columnKey]: values }));
    setCurrentPage(0);
  }, []);

  const handleOpenRecord = useCallback((item: RoadblockRecord) => {
    setSelectedRecordId(item.id);
  }, []);

  const handleRecordGone = useCallback(() => {
    setSelectedRecordId(null);
    showToast({
      header: '',
      subtext: t('route_roadblocks.record_gone_message'),
      variant: 'info',
      position: 'bottom',
      duration: 4000,
    });
    loadRecentSubmissions();
  }, [loadRecentSubmissions, showToast, t]);

  const handleRetryPress = useCallback((): void => {
    void loadRecentSubmissions();
  }, [loadRecentSubmissions]);

  const handleReportRouteIssuePress = useCallback(() => {
    routeCardRef.current?.measureLayout(
      scrollRef.current as any,
      (_x, y) => {
        scrollRef.current?.scrollTo({ y, animated: true });
        setTimeout(() => setRouteDropdownOpenTrigger(prev => prev + 1), 250);
      },
      () => {
        setRouteDropdownOpenTrigger(prev => prev + 1);
      },
    );
  }, []);

  const pageData = filteredData;

  const customerReady = selectedCustomer !== null;

  const noRoutesMsg = isLoadingRoutes
    ? t('route_roadblocks.placeholder_loading_routes')
    : t('route_roadblocks.placeholder_no_routes_today');

  // Quick filter pills config — built from t() so they re-render on language change
  const pillConfig = useMemo(() => [
    { key: 'all' as QuickFilter,   label: t('route_roadblocks.quick_filters.all') },
    { key: 'today' as QuickFilter, label: t('route_roadblocks.quick_filters.today') },
    { key: 'last7' as QuickFilter, label: t('route_roadblocks.quick_filters.last7') },
    { key: 'stop' as QuickFilter,  label: t('route_roadblocks.quick_filters.stop') },
    { key: 'route' as QuickFilter, label: t('route_roadblocks.quick_filters.route') },
  ], [t]);

  return (
    <View style={styles.root}>
    <DetailsTooltip
      visible={hoveredDetails !== null}
      position={hoveredDetails}
      text={hoveredDetails?.text ?? ''}
    />
    <ScrollView ref={scrollRef} style={{ flex: 1 }} contentContainerStyle={styles.scrollContent} keyboardShouldPersistTaps="handled">

      {/* ── Not-scheduled modal ────────────────────────────────────────────── */}
      <Modal visible={ssccNotScheduledModal} transparent animationType="fade">
        <View style={styles.modalOverlay}>
          <View style={styles.modalBox}>
            <View style={styles.modalHead}>
              <Text style={styles.modalTitle}>{t('route_roadblocks.modal_not_scheduled_title')}</Text>
            </View>
            <View style={styles.modalBody}>
              <View style={styles.warnBanner}>
                <MaterialIcons name="warning" size={20} color="#E5A408" style={{ flexShrink: 0 }} />
                <Text style={styles.warnBannerText}>{ssccNotScheduledMessage}</Text>
              </View>
              <Text style={styles.fine}>{t('route_roadblocks.modal_not_scheduled_fine')}</Text>
            </View>
            <View style={styles.modalFoot}>
              <TouchableOpacity style={styles.modalBtnSecondary} disabled={proceedValidating} onPress={() => {
                setSsccNotScheduledModal(false);
                if (pendingCustomer) {
                  setPendingCustomer(null);
                }
              }}>
                <Text style={styles.modalBtnSecondaryText}>{t('route_roadblocks.modal_action_back')}</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.modalBtnPrimary} onPress={handleProceed} disabled={proceedValidating}>
                {proceedValidating
                  ? <ActivityIndicator size="small" color="#fff" />
                  : <Text style={styles.modalBtnPrimaryText}>{t('route_roadblocks.modal_action_proceed')}</Text>}
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      {/* ── Read-only banner ───────────────────────────────────────────────── */}
      {!isFullAccess && (
        <View style={styles.readNote}>
          <EyeIcon />
          <Text style={styles.readNoteText}>
            {t('route_roadblocks.readonly_banner')}
          </Text>
        </View>
      )}

      {/* ── Report Stop Issue ──────────────────────────────────────────────── */}
      {isFullAccess && (
        <View style={styles.cardStopIssue}>
          <View style={styles.cardHeadRow}>
            <View style={styles.cardHeadCol}>
              <Text style={styles.cardTitle}>{t('route_roadblocks.card_stop_issue_title')}</Text>
              <Text style={styles.cardSub}>{t('route_roadblocks.card_stop_issue_sub')}</Text>
            </View>
          </View>

          <View style={styles.ssccBox}>
            <Text style={styles.fieldLabel}>{t('route_roadblocks.switcher_by_customer')}</Text>
            <View style={styles.taWrap}>
              <View style={styles.ssccWrap} ref={customerInputWrapRef}>
                <TextInput
                  style={styles.textInput}
                  value={customerText}
                  onChangeText={handleCustomerTextChange}
                  placeholder={t('route_roadblocks.placeholder_customer_search')}
                  placeholderTextColor="#838383"
                  onFocus={() => {
                    if (customerText.trim() && !selectedCustomer && !customerDropOpen) {
                      if (Platform.OS !== 'web') measureCustomerDropdown();
                      setCustomerDropOpen(true);
                    }
                  }}
                  onBlur={() => { if (customerDebounceRef.current) clearTimeout(customerDebounceRef.current); setTimeout(() => { if (!customerSelectingRef.current) setCustomerDropOpen(false); customerSelectingRef.current = false; }, 200); }}
                />
                {customerValidating
                  ? <ActivityIndicator size="small" color={dc("#005CBC")} style={{ marginLeft: 8 }} />
                  : <TouchableOpacity style={[styles.goArrow, customerReady && styles.goArrowReady]} onPress={handleCustomerSubmit} disabled={!customerReady || customerValidating}>
                      <GoArrowIcon ready={customerReady} />
                    </TouchableOpacity>
                }
              </View>
              {!!customerError && (
                <Text style={styles.errorText}>{customerError}</Text>
              )}
              {/* Web: Absolute positioned dropdown */}
              {Platform.OS === 'web' && customerDropOpen && (
                <View style={styles.taDropdown}>
                  {customerLoading
                    ? <ActivityIndicator size="small" color={dc("#005CBC")} style={{ padding: 8 }} />
                    : customerOptions.length === 0
                      ? <Text style={styles.taEmpty}>{customerEmptyMsg ?? t('route_roadblocks.empty_customers')}</Text>
                      : <ScrollView
                          style={styles.taList}
                          keyboardShouldPersistTaps="handled"
                          nestedScrollEnabled
                          showsVerticalScrollIndicator
                        >
                          {customerOptions.map((opt, idx) => {
                            const label = formatCustomerDisplay(opt.customerCofId, opt.customerName, opt.displayLabel);
                            return (
                              <Pressable key={opt.customerCofId ?? idx} style={styles.taOption} onPressIn={() => { customerSelectingRef.current = true; }} onPress={() => handleSelectCustomer(opt)}>
                                {opt.customerCofId ? <Text style={styles.taCofCol} numberOfLines={1}>{opt.customerCofId}</Text> : null}
                                {opt.customerCofId ? <Text style={styles.taSep}>-</Text> : null}
                                <Text style={styles.taName} numberOfLines={1}>{opt.customerName ?? label}</Text>
                              </Pressable>
                            );
                          })}
                        </ScrollView>
                  }
                </View>
              )}

              {/* Mobile: Modal overlay keeps the dropdown above the ScrollView's clipping bounds (same pattern as DateRangeFilterDropdown) */}
              {Platform.OS !== 'web' && customerDropOpen && (
                <Modal
                  visible={customerDropOpen}
                  transparent
                  animationType="none"
                  onRequestClose={() => setCustomerDropOpen(false)}
                >
                  <TouchableWithoutFeedback onPress={() => setCustomerDropOpen(false)}>
                    <View style={{ flex: 1 }}>
                      <TouchableWithoutFeedback onPress={() => { /* stop propagation */ }}>
                        <View
                          style={[
                            styles.taDropdown,
                            {
                              position: 'absolute',
                              top: customerDropPos.top,
                              left: customerDropPos.left,
                              width: customerDropPos.width,
                              maxHeight: 300,
                            },
                          ]}
                        >
                          {customerLoading
                            ? <ActivityIndicator size="small" color="#005CBC" style={{ padding: 8 }} />
                            : customerOptions.length === 0
                              ? <Text style={styles.taEmpty}>{customerEmptyMsg ?? t('route_roadblocks.empty_customers')}</Text>
                              : <ScrollView
                                  style={[styles.taList, { maxHeight: 300 }]}
                                  keyboardShouldPersistTaps="handled"
                                  nestedScrollEnabled
                                  showsVerticalScrollIndicator
                                >
                                  {customerOptions.map((opt, idx) => {
                                    const label = formatCustomerDisplay(opt.customerCofId, opt.customerName, opt.displayLabel);
                                    return (
                                      <Pressable
                                        key={opt.customerCofId ?? idx}
                                        style={styles.taOption}
                                        onPress={() => {
                                          handleSelectCustomer(opt);
                                          setCustomerDropOpen(false);
                                        }}
                                      >
                                        {opt.customerCofId ? <Text style={styles.taCofCol} numberOfLines={1}>{opt.customerCofId}</Text> : null}
                                        {opt.customerCofId ? <Text style={styles.taSep}>-</Text> : null}
                                        <Text style={styles.taName} numberOfLines={1}>{opt.customerName ?? label}</Text>
                                      </Pressable>
                                    );
                                  })}
                                </ScrollView>
                          }
                        </View>
                      </TouchableWithoutFeedback>
                    </View>
                  </TouchableWithoutFeedback>
                </Modal>
              )}
            </View>
          </View>
        </View>
      )}

      {/* ── Report Route Issue ─────────────────────────────────────────────── */}
      {isFullAccess && (
        <View ref={routeCardRef} style={styles.cardRouteIssue}>
          <View style={styles.cardHeadRow}>
            <View style={styles.cardHeadCol}>
              <Text style={styles.cardTitle}>{t('route_roadblocks.card_route_issue_title')}</Text>
              <Text style={styles.cardSub}>{t('route_roadblocks.card_route_issue_sub')}</Text>
            </View>
            {/* TODO: Uncomment when date-wise route availability is supported.
              Present Day: shows routes out for delivery today
              Historical: covers the past rolling 7 days
              Currently disabled — will be re-enabled once backend supports fetching routes by date range.
            <View style={styles.switcher}>
              <Pressable style={[styles.swOpt, routeTimeframe === 'present' && styles.swOptActive]} onPress={() => { setRouteTimeframe('present'); setSelectedRoute(''); setSelectedRouteItem(null); }}>
                <Text style={[styles.swOptText, routeTimeframe === 'present' && styles.swOptTextActive]}>{t('route_roadblocks.switcher_present_day')}</Text>
              </Pressable>
              <Pressable style={[styles.swOpt, routeTimeframe === 'historical' && styles.swOptActive]} onPress={() => { setRouteTimeframe('historical'); setSelectedRoute(''); setSelectedRouteItem(null); }}>
                <Text style={[styles.swOptText, routeTimeframe === 'historical' && styles.swOptTextActive]}>{t('route_roadblocks.switcher_historical')}</Text>
              </Pressable>
            </View>
            */}
          </View>

          <View style={[styles.ssccRow, { alignItems: 'flex-end' }]}>
            <View style={styles.routeSection}>
              <HarborDropdown
                label={t('route_roadblocks.label_select_route')}
                isRequired={false}
                placeholder={isLoadingRoutes ? t('route_roadblocks.placeholder_loading_routes') : routeOptions.length === 0 ? noRoutesMsg : t('route_roadblocks.placeholder_select_route')}
                childList={routeOptions.map(opt => ({ id: opt.value, displayText: opt.label }))}
                selectedValue={selectedRouteItem}
                openTrigger={routeDropdownOpenTrigger}
                setSelectedValue={(item) => {
                  if (item) {
                    setSelectedRouteItem(item as { id: string; displayText: string });
                    setSelectedRoute(String(item.id));
                  } else {
                    setSelectedRouteItem(null);
                    setSelectedRoute('');
                  }
                }}
                isClearable
                readOnly={isLoadingRoutes || routeOptions.length === 0}
                style={styles.routeDropdownStyle}
                size="medium"
              />
            </View>

            <TouchableOpacity
              style={[styles.beginBtn, (!selectedRoute || beginLoading) && styles.beginBtnDisabled]}
              onPress={handleBegin}
              disabled={!selectedRoute || beginLoading}
            >
              {beginLoading
                ? <ActivityIndicator size="small" color="#fff" />
                : <Text style={[styles.beginBtnText, !selectedRoute && styles.beginBtnTextDisabled]}>{t('route_roadblocks.btn_begin')}</Text>
              }
            </TouchableOpacity>
            {!!beginError && (
              <Text style={styles.errorText}>{beginError}</Text>
            )}
          </View>
        </View>
      )}

      {/* ── Recent Roadblocks table (desktop) ─────────────────────────────── */}
      {!isMobile && (
        <View style={styles.tableCard}>
          <View style={[styles.cardHeadRow, { paddingHorizontal: 20, paddingTop: 20, marginBottom: 0 }]}>
            <View style={styles.cardHeadCol}>
              <Text style={styles.cardTitle}>{t('route_roadblocks.recent_roadblocks_title')}</Text>
            </View>
          </View>

          <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.pillsScroll} contentContainerStyle={styles.pillsContainer}>
            {pillConfig.map(pill => (
              <Pressable
                key={pill.key}
                style={[styles.pill, quickFilter === pill.key && styles.pillActive]}
                onPress={() => handlePillPress(pill.key)}>
                <Text style={[styles.pillText, quickFilter === pill.key && styles.pillTextActive]}>{pill.label}</Text>
              </Pressable>
            ))}
          </ScrollView>

          <View style={styles.tableTop}>
            <Text style={styles.tableSummary}>{t('route_roadblocks.table_summary', { shown: pageData.length, total: totalElements })}</Text>
            <View style={styles.tableTools}>
              <View style={styles.searchWrap}>
                <MaterialIcons name="search" size={16} color={dc("#838383")} />
                <TextInput
                  style={styles.searchInput}
                  value={globalSearch}
                  onChangeText={v => {
                    setGlobalSearch(v);
                    setCurrentPage(0);
                    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
                    searchDebounceRef.current = setTimeout(() => setDebouncedSearch(v), 400);
                  }}
                  placeholder={t('route_roadblocks.placeholder_search')}
                  placeholderTextColor={dc("#838383")}
                />
              </View>
              {hasActiveFilters && (
                <TouchableOpacity onPress={clearAllFilters}>
                  <Text style={styles.clearFiltersBtn}>{t('route_roadblocks.btn_clear_filters')}</Text>
                </TouchableOpacity>
              )}
            </View>
          </View>

          {tableError ? (
            <View style={styles.readNote}>
              <MaterialIcons name="error-outline" size={18} color={AdminColors.red500} />
              <Text style={[styles.readNoteText, { color: AdminColors.red500 }]}>{tableError}</Text>
              <TouchableOpacity onPress={handleRetryPress}>
                <Text style={[styles.readNoteText, { color: dc('#005CBC'), textDecorationLine: 'underline', marginLeft: 8 }]}>{t('common.retry')}</Text>
              </TouchableOpacity>
            </View>
          ) : (tableLoading || pageData.length > 0) ? (
          <HarborDataTable
            data={pageData}
            columns={columns}
            tableHeader={t('route_roadblocks.recent_roadblocks_title')}
            tableDescription={t('route_roadblocks.table_summary', { shown: pageData.length, total: totalElements })}
            showHeaderTopBorder={false}
            onRowClick={(row) => handleOpenRecord(row as RoadblockRecord)}
            isLoading={tableLoading}
            maxRecordsToShow={pageSize}
            externalPage={currentPage}
            externalTotalPages={totalPages}
            onExternalPageChange={(newPage) => setCurrentPage(newPage)}
            onPageSizeChange={(newSize) => setPageSize(newSize)}
            onSort={handleSort}
            isFirstPage={currentPage === 0}
            isLastPage={currentPage === totalPages - 1}
            columnFilters={columnFilters}
            onColumnFilter={handleColumnFilter}
            locale={{
              rowsPerPageLabel: t('delivery_rpm.pagination.rows_per_page'),
              pageLabel: t('delivery_rpm.pagination.page_label'),
              pageOfLabel: t('delivery_rpm.pagination.page_of_label'),
              firstPageLabel: t('delivery_rpm.pagination.first_page_label'),
              prevPageLabel: t('delivery_rpm.pagination.prev_page_label'),
              nextPageLabel: t('delivery_rpm.pagination.next_page_label'),
              lastPageLabel: t('delivery_rpm.pagination.last_page_label'),
              noDataLabel: t('delivery_rpm.pagination.no_data_label'),
            }}
          />
          ) : (
            <View style={styles.emptyState}>
              <MaterialIcons name="report-problem" size={34} color={dc("#C0C0C0")} />
              <Text style={styles.emptyText}>
                {hasActiveFilters ? t('route_roadblocks.empty_filters') : t('route_roadblocks.empty_no_records')}
              </Text>
              {hasActiveFilters ? (
                <TouchableOpacity onPress={clearAllFilters}>
                  <Text style={styles.clearFiltersBtn}>{t('route_roadblocks.btn_clear_filters')}</Text>
                </TouchableOpacity>
              ) : isFullAccess ? (
                <TouchableOpacity style={styles.reportRouteIssueBtn} onPress={handleReportRouteIssuePress}>
                  <Text style={styles.reportRouteIssueBtnText}>{t('route_roadblocks.btn_report_route_issue')}</Text>
                </TouchableOpacity>
              ) : null}
            </View>
          )}
        </View>
      )}

      {/* ── Mobile card list ───────────────────────────────────────────────── */}
      {isMobile && (
        <View style={styles.tableCard}>
          <View style={[styles.cardHeadRow, { paddingHorizontal: 20, paddingTop: 20, marginBottom: 0 }]}>
            <View style={styles.cardHeadCol}>
              <Text style={styles.cardTitle}>{t('route_roadblocks.recent_roadblocks_title')}</Text>
              <Text style={styles.cardSub}>{t('route_roadblocks.recent_roadblocks_sub')}</Text>
            </View>
          </View>

          <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.pillsScroll} contentContainerStyle={styles.pillsContainer}>
            {pillConfig.map(pill => (
              <Pressable
                key={pill.key}
                style={[styles.pill, quickFilter === pill.key && styles.pillActive]}
                onPress={() => handlePillPress(pill.key)}>
                <Text style={[styles.pillText, quickFilter === pill.key && styles.pillTextActive]}>{pill.label}</Text>
              </Pressable>
            ))}
          </ScrollView>

          <View style={styles.tableTop}>
            <Text style={styles.tableSummary}>{t('route_roadblocks.table_summary', { shown: pageData.length, total: totalElements })}</Text>
            <View style={styles.tableTools}>
              <View style={styles.searchWrap}>
                <MaterialIcons name="search" size={16} color={dc("#838383")} />
                <TextInput
                  style={styles.searchInput}
                  value={globalSearch}
                  onChangeText={v => {
                    setGlobalSearch(v);
                    setCurrentPage(0);
                    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
                    searchDebounceRef.current = setTimeout(() => setDebouncedSearch(v), 400);
                  }}
                  placeholder={t('route_roadblocks.placeholder_search')}
                  placeholderTextColor={dc("#838383")}
                />
              </View>
              {hasActiveFilters && (
                <TouchableOpacity onPress={clearAllFilters}>
                  <Text style={styles.clearFiltersBtn}>{t('route_roadblocks.btn_clear_filters')}</Text>
                </TouchableOpacity>
              )}
            </View>
          </View>

          {pageData.length === 0 ? (
            <View style={styles.emptyState}>
              <MaterialIcons name="report-problem" size={34} color={dc("#C0C0C0")} />
              <Text style={styles.emptyText}>
                {hasActiveFilters ? t('route_roadblocks.empty_filters') : t('route_roadblocks.empty_no_records')}
              </Text>
              {hasActiveFilters ? (
                <TouchableOpacity onPress={clearAllFilters}>
                  <Text style={styles.clearFiltersBtn}>{t('route_roadblocks.btn_clear_filters')}</Text>
                </TouchableOpacity>
              ) : isFullAccess ? (
                <TouchableOpacity style={styles.reportRouteIssueBtn} onPress={handleReportRouteIssuePress}>
                  <Text style={styles.reportRouteIssueBtnText}>{t('route_roadblocks.btn_report_route_issue')}</Text>
                </TouchableOpacity>
              ) : null}
            </View>
          ) : (
            <>
            <View style={styles.cardList}>
              {pageData.map(r => (
                <TouchableOpacity key={r.id} style={styles.mobileCard} onPress={() => handleOpenRecord(r)}>
                  <View style={styles.mobileCardTop}>
                    <TypeBadge type={r.type} t={t} />
                    <Text style={styles.mobileDate}>{formatDate(r.date)}</Text>
                  </View>
                  <View style={styles.mobileRow}>
                    <Text style={styles.mobileLabel}>{t('route_roadblocks.mobile_label_route')}</Text>
                    <Text style={styles.mobileValue}>{r.route}</Text>
                  </View>
                  {!!r.sscc && (
                    <View style={styles.mobileRow}>
                      <Text style={styles.mobileLabel}>{t('route_roadblocks.mobile_label_sscc')}</Text>
                      <Text style={styles.mobileValue}>{r.sscc}</Text>
                    </View>
                  )}
                  <View style={styles.mobileRow}>
                    <Text style={styles.mobileLabel}>{t('route_roadblocks.mobile_label_reason')}</Text>
                    <Text style={styles.mobileValue}>{r.reason}</Text>
                  </View>
                  <View style={styles.mobileRow}>
                    <Text style={styles.mobileLabel}>{t('route_roadblocks.mobile_label_details')}</Text>
                    <Text style={styles.mobileValue} numberOfLines={2}>{r.details}</Text>
                  </View>
                  <View style={styles.mobileRow}>
                    <Text style={styles.mobileLabel}>{t('route_roadblocks.mobile_label_driver')}</Text>
                    <Text style={styles.mobileValue}>{r.driver}</Text>
                  </View>
                </TouchableOpacity>
              ))}
            </View>
            {totalPages > 1 && (
              <View style={styles.pagFoot}>
                <TouchableOpacity
                  style={[styles.pgBtn, currentPage === 0 && styles.pgBtnDisabled]}
                  onPress={() => setCurrentPage(p => Math.max(0, p - 1))}
                  disabled={currentPage === 0}
                >
                  <MaterialIcons name="chevron-left" size={16} color={currentPage === 0 ? '#BDBDBD' : '#616161'} />
                </TouchableOpacity>
                {getPageNumbers(currentPage, totalPages).map((page, idx) =>
                  page === '...'
                    ? <Text key={`ell-${idx}`} style={[styles.pgBtnText, { paddingHorizontal: 2 }]}>…</Text>
                    : <TouchableOpacity
                        key={page}
                        style={[styles.pgBtn, (page as number) === currentPage && styles.pgBtnActive]}
                        onPress={() => setCurrentPage(page as number)}
                      >
                        <Text style={[styles.pgBtnText, (page as number) === currentPage && styles.pgBtnActiveText]}>
                          {(page as number) + 1}
                        </Text>
                      </TouchableOpacity>
                )}
                <TouchableOpacity
                  style={[styles.pgBtn, currentPage === totalPages - 1 && styles.pgBtnDisabled]}
                  onPress={() => setCurrentPage(p => Math.min(totalPages - 1, p + 1))}
                  disabled={currentPage === totalPages - 1}
                >
                  <MaterialIcons name="chevron-right" size={16} color={currentPage === totalPages - 1 ? '#BDBDBD' : '#616161'} />
                </TouchableOpacity>
              </View>
            )}
            </>
          )}
        </View>
      )}
    </ScrollView>

      {/* ── Record detail modal ──────────────────────────────────────────── */}
      <RoadblockRecordModal
        recordId={selectedRecordId}
        onClose={() => setSelectedRecordId(null)}
        onRecordGone={handleRecordGone}
      />
    </View>
  );
};

export default RouteRoadblocksScreen;
